import axios from "axios";
import { toast } from "sonner";
import offlineQueue from "./offlineQueue";

/**
 * resilientPost — Attempts a critical write. On network/5xx failure,
 * queues the operation in localStorage for later flush (zero data loss).
 *
 * Returns:
 *   { ok: true, response }        when server accepted
 *   { ok: true, queued: true, id } when queued offline (caller treats as optimistic success)
 *   throws                         for client-side errors (400/401/403/404/422) — caller handles
 *
 * @param {string} url - full URL (including /api prefix)
 * @param {object} data - request body
 * @param {object} opts - { method: 'post'|'put'|'delete', label, headers, silent }
 */
export async function resilientPost(url, data, opts = {}) {
  const method = (opts.method || "post").toLowerCase();
  const label = opts.label || `${method.toUpperCase()} ${url}`;
  const headers = opts.headers || undefined;

  try {
    const response = await axios({ method, url, data, headers, timeout: 25000 });
    return { ok: true, response };
  } catch (error) {
    const isNetworkError = !error.response;
    const status = error.response?.status;
    const isServerError = status >= 500;
    const isTimeout = error.code === "ECONNABORTED" || status === 504;

    // Queue on network error, server error, or gateway timeout — server may or may not
    // have received the request. We warn the user about potential duplicates on flush.
    if (isNetworkError || isServerError || isTimeout) {
      const item = offlineQueue.enqueue({ method, url, data, label, headers });
      if (!opts.silent) {
        toast.warning("Sin conexión — guardado local. Se sincronizará automáticamente.", {
          duration: 5000,
        });
      }
      return { ok: true, queued: true, id: item.id };
    }

    // Client error (4xx) — do NOT queue, let caller handle (validation, permissions)
    throw error;
  }
}

/**
 * Flush a single queued operation. Returns true on success (removed from queue),
 * false on transient failure (kept for next attempt), throws on permanent 4xx.
 */
export async function flushOne(item) {
  try {
    await axios({
      method: item.method,
      url: item.url,
      data: item.data,
      headers: item.headers || undefined,
      timeout: 25000,
    });
    offlineQueue.remove(item.id);
    return true;
  } catch (error) {
    const isNetworkError = !error.response;
    const status = error.response?.status;
    const isServerError = status >= 500;
    const isTimeout = error.code === "ECONNABORTED" || status === 504;

    if (isNetworkError || isServerError || isTimeout) {
      // Keep in queue, try again next flush cycle
      offlineQueue.updateAttempt(item.id, error.message || "Error de red");
      return false;
    }

    // Permanent 4xx — cannot retry; remove from queue to avoid infinite re-attempts.
    // Notify user so they know the saved-locally item could not be synced (needs re-entry).
    offlineQueue.remove(item.id);
    try {
      // Lazy-import to avoid circular deps with notification libs in tests
      const { toast } = await import("sonner");
      const detail = error.response?.data?.detail || error.message || "Error";
      toast.error(`No se pudo sincronizar "${item.label}": ${detail}`, { duration: 8000 });
    } catch (_e) {
      // toast not available — swallow
    }
    throw error;
  }
}

/**
 * Flush entire queue sequentially. Returns { succeeded, failed }.
 */
export async function flushAll(onProgress) {
  const items = offlineQueue.getAll();
  let succeeded = 0;
  let failed = 0;
  for (const item of items) {
    try {
      const ok = await flushOne(item);
      if (ok) succeeded += 1;
      else failed += 1;
    } catch {
      failed += 1;
    }
    if (typeof onProgress === "function") {
      onProgress({ done: succeeded + failed, total: items.length });
    }
    // If we hit a transient failure, stop early — backend likely still down
    if (failed > 0 && !offlineQueue.getAll().find((i) => i.id === items[items.length - 1].id)) {
      // continue
    }
  }
  return { succeeded, failed };
}
